"use client";

import { cloudinaryImageTypes } from "@/apiUtils/constants";
import {
  azureCdnPath,
  azureCdnPathForBrand,
  cloudinaryFolderName,
  cloudinaryFolderNameForBrand,
} from "@/runtimeConfig/public";
import { getCloudinaryPublicId, getOriginalImageURL } from "@/utils/cloudinary";
import { CldImage as CldImageIn, CldImageProps } from "next-cloudinary";
import { StaticImageData, StaticRequire } from "next/dist/shared/lib/get-img-props";
import { useEffect, useMemo, useState } from "react";
import Image from "../imageV2/ImageV2";

interface NextImageProps extends Omit<CldImageProps, "src" | "quality" | "alt"> {
  fallbackSrc?: string;
  imageType?: string;
  width?: number;
  height?: number;
  alt?: string;
  sizes?: string;
  className?: string;
  style?: any;
  src?: string | StaticImageData;
  priority?: boolean;
  loading?: "lazy" | "eager";
  quality?: string | number;
}

interface NextJsImageProps {
  src: string;
  fallbackSrc?: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  alt: string;
  sizes?: string;
  className?: string;
  style?: any;
  quality?: number;
  loading?: "lazy" | "eager";
  unoptimized?: boolean;
}

const CloudinaryImage = ({ src, fallbackSrc, imageType, alt, ...rest }: NextImageProps): JSX.Element => {
  const fallbackImage = useMemo(() => {
    const envVariable = (process as any).env.ENV ?? (process as any).env.NEXT_PUBLIC_ENV;
    return envVariable === "SIT"
      ? "global-m-no-image-portrait_sp8io0.svg"
      : "no-image-square_xpvtmf.svg";
  }, []);

  const { actualFolderName, actualCdnPath } = useMemo(() => {
    if (imageType === cloudinaryImageTypes.BRAND) {
      return {
        actualFolderName: cloudinaryFolderNameForBrand,
        actualCdnPath: azureCdnPathForBrand,
      };
    }
    return { actualFolderName: cloudinaryFolderName, actualCdnPath: azureCdnPath };
  }, [imageType]);

  const [imgSrc, setImgSrc] = useState(
    actualFolderName + (typeof src === "string" ? src.replace(actualCdnPath, "") : ""),
  );

  useEffect(() => {
    if (typeof src === "string") {
      setImgSrc(actualFolderName + src.replace(actualCdnPath, ""));
    }
  }, [actualFolderName, src, actualCdnPath]);

  const handleError = () => {
    if (fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
    } else {
      setImgSrc(fallbackImage);
    }
  };

  // console.log({ actualCdnPath, actualFolderName });
  // console.log("src in component ->", imgSrc);

  return <CldImageIn {...rest} alt={alt ?? ""} src={imgSrc} onError={handleError} />;
};

const CloudinaryImageWithFallback = ({
  src,
  fallbackSrc,
  imageType,
  gravity,
  quality,
  format,
  loading,
  priority,
  width,
  height,
  alt,
  sizes,
  className,
  style,
  originalSrc,
  ...rest
}: NextImageProps & { originalSrc?: string }) => {
  // Rendered by next/image, so these must be local paths, not Cloudinary public IDs.
  const fallbackImage = useMemo(() => {
    const envVariable = (process as any).env.ENV ?? (process as any).env.NEXT_PUBLIC_ENV;
    return envVariable === "SIT"
      ? "/magrabi/images/no-image-portrait.svg"
      : "/magrabi/images/no-image-square.svg";
  }, []);

  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setUseFallback(false);
  }, [src]);

  const handleError = () => {
    if (!useFallback) {
      setUseFallback(true);
    }
  };

  //console.log("*************************************");
  //console.log("Logging the Cloudinary DRM attributes");
  //console.log("*************************************");
  //console.log("Image URL:", src);
  //console.log("Other properties: ", rest);

  // Filter props for Next.js Image component.
  // Prefer the original origin image over the placeholder; Image falls back to the placeholder if that fails too.
  const nextJsImageProps: NextJsImageProps = {
    src: originalSrc ?? fallbackImage,
    fallbackSrc: originalSrc ? (fallbackSrc ?? fallbackImage) : fallbackSrc,
    width,
    height,
    alt: alt ?? "",
    sizes,
    className,
    style,
    quality: 95,
    loading: loading ?? "lazy",
    unoptimized: !!originalSrc,
  };

  return (
    <>
      {useFallback ? (
        <Image {...nextJsImageProps} />
      ) : (
        <CldImageIn
          {...rest}
          alt={alt ?? ""}
          width={width}
          height={height}
          sizes={sizes}
          className={className}
          style={style}
          quality={quality ?? "auto"}
          //format={format ?? "auto"}
          loading={priority ? "eager" : (loading ?? "lazy")}
          src={typeof src === "string" ? src : ""}
          onError={handleError}
        />
      )}
    </>
  );
};

const CldImage = ({
  src,
  fallbackSrc,
  loading,
  priority,
  alt,
  ...rest
}: NextImageProps): JSX.Element => {
  /* to handle static imports */
  if (typeof src !== "string") {
    src = (src as StaticImageData)?.src ?? (src as unknown as StaticRequire)?.default?.src;
  }

  const cloudinaryDistribution =
    (process as any).env.NEXT_PUBLIC_CLOUDINARY_SECURE_DISTRIBUTION ?? "mcdn.doctor-m.com";

  if (src.includes(cloudinaryDistribution!)) {
    const publicId = getCloudinaryPublicId(src);
    return (
      <CloudinaryImageWithFallback
        {...rest}
        src={publicId}
        alt={alt}
        originalSrc={getOriginalImageURL(publicId)}
        fallbackSrc={fallbackSrc}
        priority
        loading={priority ? undefined : loading}
      />
    );
    // <CloudinaryImage
    //   {...rest}
    //   src={src}
    //   fallbackSrc={fallbackSrc}
    //   imageType={imageType}
    //   sizes="(max-width: 768px) 100vw, 768px"
    //   gravity={gravity ?? "auto"}
    //   quality={quality ?? "55"}
    //   format={format ?? "jpg"}
    //   loading={loading ?? "lazy"}
    // />
  } else {
    // Filter props for Next.js Image component
    const nextJsImageProps: NextJsImageProps = {
      src: src as string,
      fallbackSrc,
      width: rest.width,
      height: rest.height,
      alt: alt ?? "",
      sizes: rest.sizes,
      className: rest.className,
      style: rest.style,
      quality: 95,
      loading: loading ?? "lazy",
    };
    
    return <Image {...nextJsImageProps} />;
  }
};

export default CldImage;